'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../utils';
import { tabbables } from '../internal/focusTrap';
import { acquireScrollLock, releaseScrollLock } from '../internal/scrollLock';
import { acquireSiblingInert, releaseSiblingInert } from '../internal/modalInert';
import { pushModal, popModal, isTopmost } from '../internal/modalStack';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* ── Types ─────────────────────────────────────────── */

export interface ImageFieldImage {
  /** `blob:` URL minted from the cropped output. Convenient for an
   *  immediate `<img src>` preview. Stable across re-renders of the
   *  parent. Revocation depends on whether the field is controlled
   *  (see {@link ImageFieldProps.image}): in CONTROLLED mode the URL
   *  is revoked once the parent's `image` prop stops referencing it
   *  OR when ImageField unmounts (a rejected `onChange` keeps the URL
   *  alive for as long as you keep rendering it); in UNCONTROLLED
   *  mode the URL is never revoked by ImageField — the parent owns
   *  its lifetime from the moment `onChange` hands it back, including
   *  through unmount. Persist `croppedBlob` (not the URL) if you need
   *  the image to outlive ImageField's mount or the document. */
  hero?: string;
  /** Mirror of `hero` for now (kit historical shape). Same lifetime
   *  rules as `hero` — see {@link ImageFieldProps.image}. */
  cutout?: string;
  /** Raw cropped Blob handed to the parent alongside the URL. Persist
   *  THIS, not the URL, if the image needs to outlive the current
   *  page session: the parent can re-create a `blob:` URL or upload
   *  the Blob to a stable server URL. (Browsers revoke `blob:` URLs
   *  automatically when the document unloads, so the URL is only safe
   *  for the lifetime of the current page.) Added in audit-round-2
   *  to address the controlled-value-ownership concern; the previous
   *  shape passed only the URL, leaving the parent unable to persist. */
  croppedBlob?: Blob;
  uploaded_at?: string;
}

export interface ImageFieldProps extends StyledProps, UnsafeStyleProps {
  /**
   * The current image. `hero` and `cutout` are `blob:` URLs minted by
   * ImageField. Lifetime depends on whether you control the field:
   *
   * - CONTROLLED (you pass `image`, even `null`): ImageField revokes a
   *   minted URL when your `image` prop stops referencing it OR when
   *   ImageField unmounts. Replace and delete propose changes via
   *   `onChange` WITHOUT revoking up front, so rejecting an `onChange`
   *   (validation, permissions, simply not calling setState) is safe —
   *   the URL stays valid for as long as you keep rendering ImageField
   *   with it. Each new mint supersedes the previous PENDING mint (a
   *   crop the parent has not yet accepted), so repeated rejected
   *   crops do not accumulate. If you need the URL to outlive
   *   ImageField's mount (e.g. you render it elsewhere after
   *   unmounting the field), persist `image.croppedBlob` and re-create
   *   the URL — controlled-mode URLs are tied to the field's lifetime.
   *
   * - UNCONTROLLED (you omit `image` entirely): the URL is yours the
   *   moment it arrives via `onChange`. ImageField will never revoke
   *   it — including on unmount. You are responsible for cleanup.
   *
   * For persistence across the document unload, keep `image.croppedBlob`
   * (the raw cropped Blob) and either re-create a URL or upload it to a
   * stable URL — `blob:` URLs are tied to the document lifetime anyway.
   */
  image?: ImageFieldImage | null;
  onChange?: (image: ImageFieldImage | null) => void;
  disabled?: boolean;
  width?: number | string;
  height?: number | string;
  /** Alt text for the preview image. Defaults to empty string. */
  alt?: string;
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg']);

export type CropAspect = 'free' | '1:1' | '4:3' | '3:4';

export interface CropModalProps {
  open: boolean;
  imageFile: File;
  onCrop: (blob: Blob) => void;
  onClose: () => void;
  defaultAspect?: CropAspect;
}

/* ── Icons ─────────────────────────────────────────── */

const UploadIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5-8 8" />
  </svg>
);

/* ── CropModal ─────────────────────────────────────── */

const ASPECTS: CropAspect[] = ['1:1', '4:3', '3:4', 'free'];
const ASPECT_RATIOS: Record<CropAspect, number | null> = {
  '1:1': 1,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
  free: null,
};
const HANDLE_HIT = 14;
const MIN_CROP = 30;

type Corner = 'tl' | 'tr' | 'bl' | 'br';
interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function initialCropRect(cw: number, ch: number, aspectRatio: number | null): CropRect {
  const safeW = Math.max(1, cw);
  const safeH = Math.max(1, ch);
  const inset = safeW > 40 && safeH > 40 ? 20 : 0;
  const maxW = Math.max(1, safeW - inset * 2);
  const maxH = Math.max(1, safeH - inset * 2);

  if (!aspectRatio) {
    return {
      x: (safeW - maxW) / 2,
      y: (safeH - maxH) / 2,
      w: maxW,
      h: maxH,
    };
  }

  let w = maxW;
  let h = w / aspectRatio;
  if (h > maxH) {
    h = maxH;
    w = h * aspectRatio;
  }

  return {
    x: Math.max(0, (safeW - w) / 2),
    y: Math.max(0, (safeH - h) / 2),
    w: Math.max(1, Math.min(w, safeW)),
    h: Math.max(1, Math.min(h, safeH)),
  };
}

export function CropModal({
  open,
  imageFile,
  onCrop,
  onClose,
  defaultAspect = 'free',
}: CropModalProps) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const [aspect, setAspect] = React.useState<CropAspect>(defaultAspect);
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const [crop, setCrop] = React.useState<CropRect>({ x: 20, y: 20, w: 200, h: 200 });
  const [dragging, setDragging] = React.useState<{
    mode: 'move' | 'resize';
    ox?: number;
    oy?: number;
    corner?: Corner;
    lastX?: number;
    lastY?: number;
  } | null>(null);
  const [cursorStyle, setCursorStyle] = React.useState('grab');

  /* Stash onClose so modal-stack effect always sees the latest handler. */
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  });

  /* Load image */
  React.useEffect(() => {
    if (!imageFile) return;
    const img = new Image();
    const url = URL.createObjectURL(imageFile);
    img.onload = () => {
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  /* ── Modal infrastructure: scroll lock, inert, focus trap, Escape ── */
  React.useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    acquireScrollLock();
    const scrim = dialogRef.current?.closest('.j-crop-scrim') as HTMLElement | null;
    const acquiredInert = acquireSiblingInert(scrim);
    const stackToken = pushModal();

    /* Focus the first focusable element inside the dialog. */
    const dialog = dialogRef.current;
    if (dialog) {
      const first = tabbables(dialog)[0];
      (first ?? dialog)?.focus({ preventScroll: true });
    }

    const onKey = (e: KeyboardEvent) => {
      if (!isTopmost(stackToken)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;
      const focusables = tabbables(dialog);
      if (!focusables.length) {
        e.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialog.contains(active))) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      popModal(stackToken);
      releaseScrollLock();
      releaseSiblingInert(acquiredInert);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        try {
          previouslyFocused.focus({ preventScroll: true });
        } catch {
          /* noop */
        }
      }
    };
  }, [open]);

  /* Draw */
  React.useEffect(() => {
    if (!imgLoaded || !canvasRef.current || !imgRef.current) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    const scale = Math.min(420 / img.width, 320 / img.height, 1);
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    const ctx = canvas.getContext('2d')!;

    /* Audit #14 (2026-05-16): canvas crop UI uses fixed colour
       literals — NOT tokens — because the dim wash on this canvas is
       always near-black (DIM_OUTSIDE below). A theme-inverting token
       like `--color-neutral-100` would resolve to #111215 in dark mode
       and the crop rectangle would disappear against the wash.
       Codex round 1 caught this regression — the prior v4 `--blanc`
       token fell back to '#fff' which was actually correct here.
       The rgba() literals below are also intentional: canvas does not
       accept `color-mix()`, and a tokenized alpha wash would need
       per-redraw DOM measurement. If the kit ships an inverted dim
       wash for dark theme, both `cropBorder` and the wash literals
       must change together. */
    const cropBorder = '#ffffff';
    const handleFill = cropBorder;
    /* Canvas exception per audit #14 — fixed alpha washes. */
    const DIM_OUTSIDE = 'rgba(0, 0, 0, 0.45)';
    const RULE_OF_THIRDS = 'rgba(255, 255, 255, 0.25)';
    const HANDLE_STROKE = 'rgba(0, 0, 0, 0.3)';

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = DIM_OUTSIDE;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    /* Clear + redraw crop area */
    ctx.clearRect(crop.x, crop.y, crop.w, crop.h);
    ctx.drawImage(
      img,
      crop.x / scale,
      crop.y / scale,
      crop.w / scale,
      crop.h / scale,
      crop.x,
      crop.y,
      crop.w,
      crop.h,
    );
    /* Border */
    ctx.strokeStyle = cropBorder;
    ctx.lineWidth = 2;
    ctx.strokeRect(crop.x, crop.y, crop.w, crop.h);
    /* Rule of thirds */
    ctx.strokeStyle = RULE_OF_THIRDS;
    ctx.lineWidth = 0.5;
    for (let i = 1; i <= 2; i++) {
      ctx.beginPath();
      ctx.moveTo(crop.x + (crop.w * i) / 3, crop.y);
      ctx.lineTo(crop.x + (crop.w * i) / 3, crop.y + crop.h);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(crop.x, crop.y + (crop.h * i) / 3);
      ctx.lineTo(crop.x + crop.w, crop.y + (crop.h * i) / 3);
      ctx.stroke();
    }
    /* Corner handles */
    ctx.fillStyle = handleFill;
    const hs = 5;
    const corners: [number, number][] = [
      [crop.x, crop.y],
      [crop.x + crop.w, crop.y],
      [crop.x, crop.y + crop.h],
      [crop.x + crop.w, crop.y + crop.h],
    ];
    for (const [cx2, cy2] of corners) {
      ctx.beginPath();
      ctx.arc(cx2, cy2, hs, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = HANDLE_STROKE;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx2, cy2, hs, 0, Math.PI * 2);
      ctx.stroke();
    }
  }, [imgLoaded, crop]);

  /* Reset crop when aspect changes */
  React.useEffect(() => {
    if (!canvasRef.current) return;
    const cw = canvasRef.current.width || 300;
    const ch = canvasRef.current.height || 240;
    setCrop(initialCropRect(cw, ch, ASPECT_RATIOS[aspect]));
  }, [aspect, imgLoaded]);

  const handleCrop = () => {
    if (!imgRef.current || !canvasRef.current) return;
    if (crop.w <= 0 || crop.h <= 0) return;
    const img = imgRef.current;
    const scale = Math.min(420 / img.width, 320 / img.height, 1);
    const out = document.createElement('canvas');
    out.width = crop.w / scale;
    out.height = crop.h / scale;
    out
      .getContext('2d')!
      .drawImage(
        img,
        crop.x / scale,
        crop.y / scale,
        out.width,
        out.height,
        0,
        0,
        out.width,
        out.height,
      );
    out.toBlob(
      (blob) => {
        if (blob) onCrop(blob);
      },
      'image/jpeg',
      0.92,
    );
  };

  const getCorner = (mx: number, my: number): Corner | null => {
    const { x, y, w, h } = crop;
    if (Math.abs(mx - x) < HANDLE_HIT && Math.abs(my - y) < HANDLE_HIT) return 'tl';
    if (Math.abs(mx - (x + w)) < HANDLE_HIT && Math.abs(my - y) < HANDLE_HIT) return 'tr';
    if (Math.abs(mx - x) < HANDLE_HIT && Math.abs(my - (y + h)) < HANDLE_HIT) return 'bl';
    if (Math.abs(mx - (x + w)) < HANDLE_HIT && Math.abs(my - (y + h)) < HANDLE_HIT) return 'br';
    return null;
  };

  const getCursor = (mx: number, my: number): string => {
    const corner = getCorner(mx, my);
    if (corner === 'tl' || corner === 'br') return 'nwse-resize';
    if (corner === 'tr' || corner === 'bl') return 'nesw-resize';
    if (mx >= crop.x && mx <= crop.x + crop.w && my >= crop.y && my <= crop.y + crop.h)
      return dragging ? 'grabbing' : 'grab';
    return 'default';
  };

  const canvasCoords = (e: React.PointerEvent): [number, number] => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const cw = canvas.width;
    const ch = canvas.height;
    return [
      Math.max(0, Math.min((e.clientX - rect.left) * sx, cw)),
      Math.max(0, Math.min((e.clientY - rect.top) * sy, ch)),
    ];
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const [mx, my] = canvasCoords(e);
    const corner = getCorner(mx, my);
    if (corner) {
      setDragging({ mode: 'resize', corner, lastX: mx, lastY: my });
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (mx >= crop.x && mx <= crop.x + crop.w && my >= crop.y && my <= crop.y + crop.h) {
      setDragging({ mode: 'move', ox: mx - crop.x, oy: my - crop.y });
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const [mx, my] = canvasCoords(e);
    if (!dragging) {
      setCursorStyle(getCursor(mx, my));
      return;
    }

    const ar = ASPECT_RATIOS[aspect];
    const cw = canvasRef.current!.width;
    const ch = canvasRef.current!.height;

    if (dragging.mode === 'move') {
      setCrop((c) => ({
        ...c,
        x: Math.max(0, Math.min(mx - dragging.ox!, cw - c.w)),
        y: Math.max(0, Math.min(my - dragging.oy!, ch - c.h)),
      }));
    } else if (dragging.mode === 'resize') {
      const { corner } = dragging;
      const ldx = mx - (dragging.lastX ?? mx);
      const ldy = my - (dragging.lastY ?? my);
      setDragging((d) => (d ? { ...d, lastX: mx, lastY: my } : d));

      setCrop((prev) => {
        let { x: nx, y: ny, w: nw, h: nh } = prev;
        if (corner === 'br') {
          nw += ldx;
          nh += ldy;
        } else if (corner === 'bl') {
          nx += ldx;
          nw -= ldx;
          nh += ldy;
        } else if (corner === 'tr') {
          ny += ldy;
          nw += ldx;
          nh -= ldy;
        } else if (corner === 'tl') {
          nx += ldx;
          ny += ldy;
          nw -= ldx;
          nh -= ldy;
        }

        if (ar) {
          if (Math.abs(ldx) > Math.abs(ldy)) nh = nw / ar;
          else nw = nh * ar;
          if (nx < 0) {
            nw += nx;
            nx = 0;
            nh = nw / ar;
          }
          if (ny < 0) {
            nh += ny;
            ny = 0;
            nw = nh * ar;
          }
          if (nx + nw > cw) {
            nw = cw - nx;
            nh = nw / ar;
          }
          if (ny + nh > ch) {
            nh = ch - ny;
            nw = nh * ar;
          }
          if (nw < MIN_CROP) {
            nw = MIN_CROP;
            nh = nw / ar;
          }
          if (nh < MIN_CROP) {
            nh = MIN_CROP;
            nw = nh * ar;
          }
          if (corner === 'tl') {
            nx = prev.x + prev.w - nw;
            ny = prev.y + prev.h - nh;
          } else if (corner === 'tr') {
            ny = prev.y + prev.h - nh;
          } else if (corner === 'bl') {
            nx = prev.x + prev.w - nw;
          }
        } else {
          nx = Math.max(0, nx);
          ny = Math.max(0, ny);
          nw = Math.min(Math.max(nw, MIN_CROP), cw - nx);
          nh = Math.min(Math.max(nh, MIN_CROP), ch - ny);
        }

        return { x: nx, y: ny, w: nw, h: nh };
      });
    }
  };

  const onPointerUp = () => setDragging(null);

  /* Keyboard crop manipulation (the canvas is pointer-only otherwise — no
     keyboard path to position/size the crop). Arrows MOVE the crop; Shift+arrows
     RESIZE it (maintaining the aspect ratio when one is locked). All clamped to
     the canvas + MIN_CROP. */
  const onCropKeyDown = (e: React.KeyboardEvent) => {
    const ARROWS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (!ARROWS.includes(e.key)) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cw = canvas.width || 300;
    const ch = canvas.height || 240;
    const ar = ASPECT_RATIOS[aspect];
    const step = e.shiftKey ? 6 : 8;
    const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
    setCrop((prev) => {
      let { x, y, w, h } = prev;
      if (e.shiftKey) {
        const grow = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? step : -step;
        if (ar) {
          /* Clamp width ONCE against the binding dimension — both the remaining
             horizontal space (cw - x) AND the height the ratio implies must fit
             (ch - y). Deriving h from this single clamped w keeps both axes in
             bounds (the prior "clamp w, derive h, re-derive w" could push w past
             cw - x when the h-clamp raised h). */
          const maxW = Math.min(cw - x, (ch - y) * ar);
          w = clamp(w + grow, MIN_CROP, Math.max(MIN_CROP, maxW));
          h = w / ar;
        } else {
          w = clamp(w + grow, MIN_CROP, cw - x);
          h = clamp(h + grow, MIN_CROP, ch - y);
        }
      } else {
        const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
        const dy = e.key === 'ArrowDown' ? step : e.key === 'ArrowUp' ? -step : 0;
        x = clamp(x + dx, 0, cw - w);
        y = clamp(y + dy, 0, ch - h);
      }
      return { x, y, w, h };
    });
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="j-crop-scrim">
      {/* Scrim click does NOT dismiss — per spec */}
      <div
        ref={dialogRef}
        className="j-crop-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Crop image"
        tabIndex={-1}
      >
        <div className="j-crop-header">
          <span className="j-crop-title">Crop image</span>
          <button type="button" className="j-crop-close" onClick={onClose} aria-label="Close">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        {/* The wrapper is the keyboard widget (role="application" so AT passes
            arrow keys to our handler); the canvas stays a presentational
            drawing surface. */}
        <div
          className="j-crop-canvas-wrap"
          role="application"
          /* role="application" makes this a focusable custom keyboard widget;
             tabIndex={0} is required so a keyboard user can reach it. jsx-a11y's
             no-noninteractive-tabindex doesn't recognise `application` as
             interactive — a false positive for this established pattern. */
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          aria-label="Crop region. Use the arrow keys to move the crop, and Shift + arrow keys to resize it."
          onKeyDown={onCropKeyDown}
        >
          <canvas
            ref={canvasRef}
            style={{ cursor: cursorStyle }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          />
        </div>
        <div className="j-crop-aspects" role="group" aria-label="Aspect ratio">
          {ASPECTS.map((a) => (
            <button
              key={a}
              type="button"
              className="j-crop-aspect-btn"
              data-active={a === aspect ? 'true' : undefined}
              aria-pressed={a === aspect}
              onClick={() => setAspect(a)}
            >
              {a}
            </button>
          ))}
        </div>
        <div className="j-crop-footer">
          <button type="button" className="j-btn j-btn-md j-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="j-btn j-btn-md j-btn-primary" onClick={handleCrop}>
            Crop & save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── ImageField ────────────────────────────────────── */

export function ImageField({
  image,
  onChange,
  disabled = false,
  width,
  height,
  alt = '',
  className,
  unsafe_style,
}: ImageFieldProps) {
  const [dragover, setDragover] = React.useState(false);
  const [cropFile, setCropFile] = React.useState<File | null>(null);
  const [showCutout, setShowCutout] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  /* After a crop confirms, the dropzone (or Replace button) that held focus
     unmounts as the preview renders, so CropModal's focus-restore no-ops and
     focus falls to <body>. Flag a crop so the preview moves focus to a stable
     element (Replace) instead of stranding a keyboard user. */
  const replaceBtnRef = React.useRef<HTMLButtonElement>(null);
  const focusReplaceOnPreview = React.useRef(false);

  /* Audit Finding #9 (round 5): blob: URL lifecycle. Round 2 revoked
     imperatively on user actions, breaking controlled rejection.
     Round 3 added an image-prop reconcile but eagerly fired it via
     a `reconcileTick`, racing async accepts. Round 4 dropped the
     tick — fixing the race — but introduced two leaks: an accepted
     URL whose parent batches `setImage(null)` with an unmount of
     ImageField never gets swept (the field never re-renders with
     `image=null` before going away), and a string of rejected crops
     accumulates pending mints that nothing ever cleans up.

     Round 5 tightens the tracking into two states:

       — pending: minted but never observed in `image.hero/cutout`.
         A new mint REVOKES any prior pending — the user has replaced
         their proposal, and the prior mint was never accepted.
       — accepted: observed in the parent's `image` at some point.
         The image-prop reconcile sweeps these when they drop out.

     On controlled-mode unmount we sweep BOTH sets: pending URLs are
     orphans the parent never claimed, and accepted URLs go with the
     field they were created for. Callers that need a controlled-mode
     URL to outlive ImageField's mount must persist `croppedBlob` and
     re-create the URL — `blob:` URLs are document-lifetime anyway.

     Trade-off: a rapid second mint while the parent's async accept of
     the first is still in flight (`setTimeout(() => setImage(URL1),
     N)` racing a user clicking Crop & save again) will revoke URL1
     pre-emptively. We accept that — it's a rare path, and the
     alternative (never superseding pendings) was the round-4 leak.

     UNCONTROLLED mode (`image` prop omitted entirely) is unchanged:
     the parent owns the URL from the moment `onChange` hands it over;
     ImageField never revokes. */
  const pendingMints = React.useRef<Set<string>>(new Set());
  const acceptedMints = React.useRef<Set<string>>(new Set());

  /* Derive preview URL from the image prop — no local state copy. */
  const previewUrl = showCutout
    ? image?.cutout || image?.hero || null
    : image?.hero || image?.cutout || null;

  const isControlled = image !== undefined;
  const heroUrl = image?.hero;
  const cutoutUrl = image?.cutout;
  React.useEffect(() => {
    if (!isControlled) return;
    const stillUsed = new Set<string>();
    if (heroUrl) stillUsed.add(heroUrl);
    if (cutoutUrl) stillUsed.add(cutoutUrl);
    /* Move pending URLs that the parent accepted into the accepted
       set; revoke pendings the parent left out of the new image. */
    for (const url of pendingMints.current) {
      if (stillUsed.has(url)) {
        pendingMints.current.delete(url);
        acceptedMints.current.add(url);
      } else {
        pendingMints.current.delete(url);
        URL.revokeObjectURL(url);
      }
    }
    /* Sweep accepted URLs the parent has now replaced or nulled. */
    for (const url of acceptedMints.current) {
      if (!stillUsed.has(url)) {
        acceptedMints.current.delete(url);
        URL.revokeObjectURL(url);
      }
    }
  }, [isControlled, heroUrl, cutoutUrl]);

  /* Controlled-mode unmount cleanup. Sweeps any pending mints (orphans
     the parent never accepted) and any accepted mints (the parent's
     last-known `image` state at the moment ImageField went away).
     Together these close the round-4 leaks: accepted-delete-then-
     unmount (parent batches `setImage(null)` with unmounting the
     field, so the reconcile effect never sees `image=null`) and
     accumulated-rejected-crops on long-lived fields.

     UNCONTROLLED mode is exempt — the parent owns the URL once it
     leaves via `onChange`, and ImageField has no claim on it. */
  React.useEffect(() => {
    if (!isControlled) return;
    return () => {
      /* eslint-disable react-hooks/exhaustive-deps --
         We INTEND to read the latest `.current` at unmount time
         (every URL minted between this effect's mount and the
         unmount). The rule's standard recommendation — copy
         `.current` into a local at effect-mount — would freeze the
         set at mount time and miss every subsequent mint. The refs
         are component-scoped and only mutated from this component,
         so the latest-value read is safe. */
      for (const url of pendingMints.current) URL.revokeObjectURL(url);
      pendingMints.current.clear();
      for (const url of acceptedMints.current) URL.revokeObjectURL(url);
      acceptedMints.current.clear();
      /* eslint-enable react-hooks/exhaustive-deps */
    };
  }, [isControlled]);

  const sizeStyle: React.CSSProperties = {
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    ...unsafe_style,
  };

  const handleFiles = (files: FileList | null) => {
    if (disabled || !files?.length) return;
    const file = files[0]!;
    if (!ACCEPTED_IMAGE_TYPES.has(file.type) || file.size > MAX_IMAGE_BYTES) return;
    setCropFile(file);
  };

  const closeCropModal = () => {
    setCropFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  React.useEffect(() => {
    if (disabled && cropFile) closeCropModal();
  }, [disabled, cropFile]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragover(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setDragover(true);
  };
  const onDragLeave = () => setDragover(false);

  const onCropConfirm = (blob: Blob) => {
    if (disabled) return;
    const url = URL.createObjectURL(blob);
    /* URL lifecycle tracking ONLY runs in CONTROLLED mode. In
       UNCONTROLLED mode the parent owns the URL the moment `onChange`
       hands it over and ImageField must not touch it again — no
       supersede, no tracking, no unmount sweep. This guard is the
       full contract boundary: anything that mutates pendingMints /
       acceptedMints / calls URL.revokeObjectURL belongs inside the
       `if (isControlled)` branches in this file and the effects above. */
    if (isControlled) {
      /* Supersede any previous PENDING mint: the user is replacing
         their crop proposal, and the prior one was never observed in
         the parent's `image` (else it would have moved to
         acceptedMints). Without this, repeated rejected crops would
         accumulate indefinitely. Accepted URLs are untouched — they
         sit in the parent's `image` state and the reconcile effect
         handles them when `image` next transitions. */
      for (const prev of pendingMints.current) URL.revokeObjectURL(prev);
      pendingMints.current.clear();
      pendingMints.current.add(url);
    }
    closeCropModal();
    focusReplaceOnPreview.current = true;
    onChange?.({
      ...image,
      hero: url,
      cutout: url,
      croppedBlob: blob,
      uploaded_at: new Date().toISOString(),
    });
  };

  const handleDelete = () => {
    if (disabled) return;
    /* Same contract: do not pre-emptively revoke. If the parent
       rejects the delete (validation, permissions, ignored call) the
       URL must keep working. The reconcile effect revokes once
       `image.hero` / `image.cutout` drop the URL. */
    onChange?.(null);
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept="image/png,image/jpeg"
      disabled={disabled}
      style={{ display: 'none' }}
      onChange={(e) => {
        handleFiles(e.target.files);
        e.currentTarget.value = '';
      }}
    />
  );

  const openFilePicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  /* When a crop has just confirmed and the preview is now showing, move focus
     to the Replace button so a keyboard user is not stranded on <body>. */
  React.useEffect(() => {
    if (previewUrl && focusReplaceOnPreview.current) {
      focusReplaceOnPreview.current = false;
      /* Only rescue focus if the crop actually stranded it on <body> (the
         focused dropzone/Replace unmounted). If focus already sits on a real
         element — e.g. an unrelated previewUrl change from the Cutout/Original
         toggle, or a parent that rejected the crop — do NOT steal it. */
      const active = document.activeElement;
      if (!active || active === document.body) {
        replaceBtnRef.current?.focus({ preventScroll: true });
      }
    }
  }, [previewUrl]);

  if (previewUrl) {
    return (
      <div
        className={cx('j-imagefield-preview', className)}
        style={sizeStyle}
        aria-label="Image preview"
      >
        <img src={previewUrl} alt={alt} />
        {image?.uploaded_at && (
          <div className="j-imagefield-meta">
            Uploaded {new Date(image.uploaded_at).toLocaleDateString()}
          </div>
        )}
        {!disabled && (
          <div className="j-imagefield-actions">
            {image?.cutout && image?.hero && (
              <button
                type="button"
                className="j-imagefield-action-btn"
                onClick={() => setShowCutout(!showCutout)}
              >
                {showCutout ? 'Original' : 'Cutout'}
              </button>
            )}
            <button
              ref={replaceBtnRef}
              type="button"
              className="j-imagefield-action-btn"
              onClick={openFilePicker}
            >
              Replace
            </button>
            <button
              type="button"
              className="j-imagefield-action-btn j-imagefield-action-btn-destructive"
              onClick={handleDelete}
            >
              Delete
            </button>
          </div>
        )}
        {hiddenInput}
        {cropFile && (
          <CropModal open imageFile={cropFile} onCrop={onCropConfirm} onClose={closeCropModal} />
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={cx('j-imagefield-dropzone', className)}
        data-dragover={dragover || undefined}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-label="Upload image"
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFilePicker();
          }
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={sizeStyle}
      >
        <div className="j-imagefield-dropzone-icon">
          <UploadIcon />
        </div>
        <div className="j-imagefield-dropzone-label">Drop an image or click to browse</div>
        <div className="j-imagefield-dropzone-hint">PNG, JPG up to 10 MB</div>
      </div>
      {hiddenInput}
      {cropFile && (
        <CropModal open imageFile={cropFile} onCrop={onCropConfirm} onClose={closeCropModal} />
      )}
    </>
  );
}
